import React, { useState, useEffect, useMemo } from 'react';
import {
    Table, Tag, Button, Card, Typography, Space, Row, Col, Select, DatePicker,
    Tabs, Input, Modal, message, Badge, Tooltip, Divider, Grid, List
} from 'antd';
import {
    ShoppingOutlined, TruckOutlined, CheckCircleOutlined, CloseCircleOutlined,
    EyeOutlined, EditOutlined, SearchOutlined, EnvironmentOutlined, DollarOutlined,
    ContainerOutlined, UserOutlined
} from '@ant-design/icons';
import LayoutHome from '../../components/layout/LayoutHome';
import { useAuth } from '../../aws/AuthContext';
import { useZonas } from '../../hooks/useZonas';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Pedidos = () => {
    const screens = useBreakpoint();
    const isMobile = screens.md === false;
    const { getJwtToken } = useAuth();
    const { zonas = [] } = useZonas();

    const [ordenes, setOrdenes] = useState([]);
    const [consolidado, setConsolidado] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [filtroZona, setFiltroZona] = useState('todas');

    // Modal Ver/Editar Orden
    const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
    const [detallesOrden, setDetallesOrden] = useState([]);
    const [isModalDetalleOpen, setIsModalDetalleOpen] = useState(false);
    const [loadingDetalles, setLoadingDetalles] = useState(false);

    const [vendedores, setVendedores] = useState([]);
    const [repartidorSeleccionado, setRepartidorSeleccionado] = useState(null);

    const fetchVendedores = async () => {
        try {
            const token = await getJwtToken();
            const res = await axios.get(`${API_BASE_URL}/v1-usuarios?tipo=lista`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const listaUsuarios = Array.isArray(res.data) ? res.data : [];
            setVendedores(listaUsuarios);
        } catch (error) {
            console.error("Error al cargar lista de usuarios/repartidores", error);
        }
    };

    // Cargar Órdenes
    const fetchOrdenes = async () => {
        setLoading(true);
        try {
            const token = await getJwtToken();
            const res = await axios.get(`${API_BASE_URL}/v1-ordenes-venta`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrdenes(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            message.error("No se pudieron cargar las órdenes de venta.");
        } finally {
            setLoading(false);
        }
    };

    // Cargar Consolidado de Mercancía por Zona
    const fetchConsolidado = async () => {
        try {
            const token = await getJwtToken();
            const res = await axios.get(`${API_BASE_URL}/v1-ordenes-venta?tipo=consolidado`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConsolidado(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error al cargar consolidado", error);
        }
    };

    useEffect(() => {
        fetchOrdenes();
        fetchConsolidado();
        fetchVendedores();
    }, []);

    // Cargar detalles de una orden específica
    const handleVerDetalles = async (orden) => {
        setOrdenSeleccionada(orden);
        setIsModalDetalleOpen(true);
        setLoadingDetalles(true);
        try {
            const token = await getJwtToken();
            const res = await axios.get(`${API_BASE_URL}/v1-ordenes-venta?orden_id=${orden.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDetallesOrden(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            message.error("Error al cargar los productos del pedido.");
        } finally {
            setLoadingDetalles(false);
        }
    };

    // Cambiar estado de la orden
    const handleCambiarEstado = async (ordenId, nuevoEstado) => {
        try {
            const token = await getJwtToken();
            await axios.put(`${API_BASE_URL}/v1-ordenes-venta/${ordenId}`, {
                estado: nuevoEstado,
                repartidor_id: repartidorSeleccionado || ordenSeleccionada?.usuario_id
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            message.success(`Pedido actualizado a: ${nuevoEstado.toUpperCase()}`);
            setIsModalDetalleOpen(false);
            setRepartidorSeleccionado(null);
            fetchOrdenes();
            fetchConsolidado();
        } catch (error) {
            message.error("No se pudo actualizar el estado del pedido.");
        }
    };

    // Filtros de órdenes
    const ordenesFiltradas = useMemo(() => {
        return ordenes.filter(o => {
            const coincideBusqueda =
                o.cliente_nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
                o.cliente_comercial?.toLowerCase().includes(busqueda.toLowerCase()) ||
                o.id?.toString().includes(busqueda);

            const coincideEstado = filtroEstado === 'todos' || o.estado === filtroEstado;
            const coincideZona = filtroZona === 'todas' || Number(o.zona_id) === Number(filtroZona);

            return coincideBusqueda && coincideEstado && coincideZona;
        });
    }, [ordenes, busqueda, filtroEstado, filtroZona]);

    // KPIs
    const totalPendientes = ordenes.filter(o => o.estado === 'pendiente').length;
    const totalEnRuta = ordenes.filter(o => o.estado === 'en_ruta').length;
    const totalCompletados = ordenes.filter(o => o.estado === 'completado').length;
    const montoPorEntregar = ordenes
        .filter(o => o.estado === 'pendiente' || o.estado === 'en_ruta')
        .reduce((acc, curr) => acc + Number(curr.total || 0), 0);

    const getTagEstado = (estado) => {
        switch (estado) {
            case 'pendiente': return <Tag color="warning" icon={<ShoppingOutlined />}>PENDIENTE</Tag>;
            case 'en_ruta': return <Tag color="processing" icon={<TruckOutlined />}>EN RUTA</Tag>;
            case 'completado': return <Tag color="success" icon={<CheckCircleOutlined />}>ENTREGADO</Tag>;
            case 'cancelado': return <Tag color="error" icon={<CloseCircleOutlined />}>CANCELADO</Tag>;
            default: return <Tag>{estado}</Tag>;
        }
    };

    return (
        <LayoutHome pageSeleccionada="pedidos">
            <div style={{ padding: isMobile ? '8px' : '16px', background: '#f8fafc', minHeight: '100%' }}>

                {/* HEADER Y KPIS */}
                <div style={{ marginBottom: '16px' }}>
                    <Row gutter={[12, 12]}>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ borderRadius: '12px', borderLeft: '4px solid #f59e0b' }}>
                                <Text type="secondary" style={{ fontSize: '11px', fontWeight: 700 }}>PENDIENTES</Text>
                                <Title level={3} style={{ margin: 0, color: '#f59e0b' }}>{totalPendientes}</Title>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ borderRadius: '12px', borderLeft: '4px solid #3b82f6' }}>
                                <Text type="secondary" style={{ fontSize: '11px', fontWeight: 700 }}>EN RUTA DE REPARTO</Text>
                                <Title level={3} style={{ margin: 0, color: '#3b82f6' }}>{totalEnRuta}</Title>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
                                <Text type="secondary" style={{ fontSize: '11px', fontWeight: 700 }}>ENTREGADOS</Text>
                                <Title level={3} style={{ margin: 0, color: '#10b981' }}>{totalCompletados}</Title>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" style={{ borderRadius: '12px', borderLeft: '4px solid #8b5cf6' }}>
                                <Text type="secondary" style={{ fontSize: '11px', fontWeight: 700 }}>VALOR EN RUTA</Text>
                                <Title level={4} style={{ margin: 0, color: '#8b5cf6' }}>${montoPorEntregar.toLocaleString('es-CO')}</Title>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* PESTAÑAS Y TABLAS */}
                <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <Tabs defaultActiveKey="1">

                        {/* TAB 1: LISTADO DE ÓRDENES */}
                        <Tabs.TabPane tab={<span><ShoppingOutlined /> Órdenes de Pedido</span>} key="1">

                            <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
                                <Col xs={24} sm={8}>
                                    <Text strong style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                        BUSCADOR RÁPIDO
                                    </Text>
                                    <Input
                                        placeholder="Buscar cliente, tienda o # orden..."
                                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                                        value={busqueda}
                                        onChange={e => setBusqueda(e.target.value)}
                                        allowClear
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Text strong style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                        ESTADO DE LA ORDEN
                                    </Text>
                                    <Select
                                        value={filtroEstado}
                                        onChange={setFiltroEstado}
                                        style={{ width: '100%', borderRadius: '8px' }}
                                        options={[
                                            { value: 'todos', label: 'Todos los Estados' },
                                            { value: 'pendiente', label: '🟠 Pendientes' },
                                            { value: 'en_ruta', label: '🔵 En Ruta (Despachados)' },
                                            { value: 'completado', label: '🟢 Entregados' },
                                            { value: 'cancelado', label: '🔴 Cancelados' }
                                        ]}
                                    />
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Text strong style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                        ZONA / RUTA DE ENTREGA
                                    </Text>
                                    <Select
                                        value={filtroZona}
                                        onChange={setFiltroZona}
                                        style={{ width: '100%', borderRadius: '8px' }}
                                        options={[
                                            { value: 'todas', label: 'Todas las Zonas' },
                                            ...zonas.map(z => ({ value: Number(z.id), label: `📍 ${z.nombre}` }))
                                        ]}
                                    />
                                </Col>
                            </Row>

                            {/* TABLA DE ÓRDENES */}
                            {isMobile ? (
                                /* VISTA CARDS PARA MÓVIL */
                                <List
                                    dataSource={ordenesFiltradas}
                                    loading={loading}
                                    renderItem={(r) => (
                                        <Card
                                            size="small"
                                            style={{ marginBottom: '10px', borderRadius: '10px', borderLeft: `4px solid ${r.estado === 'pendiente' ? '#f59e0b' : r.estado === 'en_ruta' ? '#3b82f6' : '#10b981'}` }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                <Text strong style={{ color: '#f59e0b' }}>#{r.id}</Text>
                                                {getTagEstado(r.estado)}
                                            </div>
                                            <Title level={5} style={{ margin: 0 }}>{r.cliente_comercial || r.cliente_nombre}</Title>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>👤 {r.cliente_nombre}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>📍 {r.zona_nombre || 'General'} | 📅 {dayjs(r.fecha_entrega).format('DD/MM/YYYY')}</Text>

                                            <Divider style={{ margin: '8px 0' }} />

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>TOTAL ORDEN</Text>
                                                    <Text strong style={{ fontSize: '16px', color: '#0f172a' }}>${Number(r.total).toLocaleString('es-CO')}</Text>
                                                </div>
                                                <Button
                                                    type="primary"
                                                    size="middle"
                                                    onClick={() => handleVerDetalles(r)}
                                                    style={{ backgroundColor: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}
                                                >
                                                    Gestionar
                                                </Button>
                                            </div>
                                        </Card>
                                    )}
                                />
                            ) : (
                                <Table
                                    dataSource={ordenesFiltradas}
                                    loading={loading}
                                    rowKey="id"
                                    scroll={{ x: 600 }}
                                    pagination={{ pageSize: 8 }}
                                    columns={[
                                        {
                                            title: 'ORDEN #',
                                            dataIndex: 'id',
                                            render: (id) => <Text strong style={{ color: '#f59e0b' }}>#{id}</Text>
                                        },
                                        {
                                            title: 'CLIENTE',
                                            key: 'cliente',
                                            render: (_, r) => (
                                                <div>
                                                    <Text strong style={{ display: 'block', fontSize: '13px' }}>
                                                        {r.cliente_comercial || r.cliente_nombre}
                                                    </Text>
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>{r.cliente_nombre}</Text>
                                                </div>
                                            )
                                        },
                                        {
                                            title: 'ZONA',
                                            dataIndex: 'zona_nombre',
                                            render: (z) => z ? <Tag color="cyan">📍 {z}</Tag> : <Text type="secondary">General</Text>
                                        },
                                        {
                                            title: 'F. ENTREGA',
                                            dataIndex: 'fecha_entrega',
                                            render: (f) => f ? dayjs(f).format('DD/MM/YYYY') : 'N/A'
                                        },
                                        {
                                            title: 'TOTAL',
                                            dataIndex: 'total',
                                            align: 'right',
                                            render: (val) => <Text strong style={{ fontSize: '14px' }}>${Number(val).toLocaleString('es-CO')}</Text>
                                        },
                                        {
                                            title: 'ESTADO',
                                            dataIndex: 'estado',
                                            align: 'center',
                                            render: (e) => getTagEstado(e)
                                        },
                                        {
                                            title: 'ACCIÓN',
                                            key: 'accion',
                                            align: 'center',
                                            render: (_, r) => (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => handleVerDetalles(r)}
                                                    style={{ backgroundColor: '#22BABB', borderRadius: '6px', fontWeight: 'bold' }}
                                                >
                                                    Gestionar
                                                </Button>
                                            )
                                        }
                                    ]}
                                />)}
                        </Tabs.TabPane>

                        {/* TAB 2: MERCANCÍA REQUERIDA POR ZONA */}
                        <Tabs.TabPane tab={<span><ContainerOutlined /> Mercancía por Cargar (Bodega)</span>} key="2">
                            <div style={{ marginBottom: '16px' }}>
                                <Text type="secondary">
                                    Consolidado de unidades necesarias para cargar en los vehículos de reparto según los pedidos pendientes y en ruta.
                                </Text>
                            </div>

                            <Row gutter={[16, 16]}>
                                {/* Agrupamos localmente por Zona */}
                                {Object.entries(
                                    consolidado.reduce((acc, curr) => {
                                        const zona = curr.zona_nombre || 'General (Sin Zona)';
                                        if (!acc[zona]) acc[zona] = [];
                                        acc[zona].push(curr);
                                        return acc;
                                    }, {})
                                ).map(([zonaNombre, items]) => (
                                    <Col xs={24} md={12} key={zonaNombre}>
                                        <Card
                                            size="small"
                                            title={<span style={{ color: '#0284c7', fontWeight: 800 }}>📍 RUTA: {zonaNombre.toUpperCase()}</span>}
                                            style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}
                                        >
                                            <Table
                                                dataSource={items}
                                                rowKey={(r) => `${r.zona_id}_${r.producto_id}`}
                                                pagination={false}
                                                size="small"
                                                columns={[
                                                    {
                                                        title: 'PRODUCTO / INSUMO',
                                                        dataIndex: 'producto_nombre',
                                                        render: n => <Text strong style={{ fontSize: '13px' }}>{n}</Text>
                                                    },
                                                    {
                                                        title: 'UNIDADES A CARGAR',
                                                        dataIndex: 'cantidad_total',
                                                        align: 'right',
                                                        render: cant => (
                                                            <Tag color="volcano" style={{ fontSize: '13px', fontWeight: 'bold', padding: '2px 8px' }}>
                                                                {cant} u.
                                                            </Tag>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Tabs.TabPane>

                    </Tabs>
                </Card>

                {/* MODAL DETALLE / GESTIÓN DE LA ORDEN */}
                <Modal
                    title={<Space><ShoppingOutlined style={{ color: '#f59e0b' }} /><Title level={4} style={{ margin: 0 }}>Gestión de Orden #{ordenSeleccionada?.id}</Title></Space>}
                    open={isModalDetalleOpen}
                    onCancel={() => {
                        setIsModalDetalleOpen(false);
                        setRepartidorSeleccionado(null);
                    }}
                    footer={null}
                    width={580}
                    centered
                >
                    {ordenSeleccionada && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>

                            {/* FICHA CLIENTE, RUTA Y USUARIO CREADOR */}
                            <Card size="small" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                                <Row gutter={[8, 8]}>
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>CLIENTE</Text>
                                        <Text strong>{ordenSeleccionada.cliente_comercial || ordenSeleccionada.cliente_nombre}</Text>
                                    </Col>
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>ZONA / RUTA</Text>
                                        <Tag color="cyan">📍 {ordenSeleccionada.zona_nombre || 'General'}</Tag>
                                    </Col>
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>TELÉFONO</Text>
                                        <Text>{ordenSeleccionada.cliente_telefono || 'N/A'}</Text>
                                    </Col>
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>DIRECCIÓN</Text>
                                        <Text>{ordenSeleccionada.cliente_direccion || 'N/A'}</Text>
                                    </Col>

                                    {/* 👤 USUARIO / VENDEDOR QUE CREÓ LA ORDEN */}
                                    <Col span={24} style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '6px', marginTop: '2px' }}>
                                        <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>TOMADO / CREADO POR:</Text>
                                        <Tag icon={<UserOutlined />} color="purple" style={{ marginTop: '2px', fontWeight: 'bold' }}>
                                            {(() => {
                                                const creadorId = ordenSeleccionada.usuario_id || ordenSeleccionada.vendedor_id;
                                                const encontrado = vendedores.find(v => String(v.id) === String(creadorId));
                                                if (encontrado) {
                                                    return `${encontrado.nombre || ''} ${encontrado.apellidos || ''}`.trim();
                                                }
                                                return ordenSeleccionada.vendedor_nombre || ordenSeleccionada.usuario_nombre || 'Vendedor en Calle';
                                            })()}
                                        </Tag>
                                    </Col>
                                </Row>
                            </Card>

                            {/* LISTA DE PRODUCTOS SOLICITADOS */}
                            <Text strong>Productos Solicitados:</Text>
                            <Table
                                dataSource={detallesOrden}
                                loading={loadingDetalles}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                columns={[
                                    { title: 'PRODUCTO', dataIndex: 'producto_nombre' },
                                    { title: 'CANT.', dataIndex: 'cantidad', align: 'center', render: c => <Text strong>{c}</Text> },
                                    { title: 'PRECIO', dataIndex: 'precio_unitario', align: 'right', render: p => `$${Number(p).toLocaleString('es-CO')}` },
                                    { title: 'SUBTOTAL', dataIndex: 'subtotal', align: 'right', render: s => <Text strong>${Number(s).toLocaleString('es-CO')}</Text> }
                                ]}
                            />

                            {ordenSeleccionada.notas && (
                                <div style={{ backgroundColor: '#fffbe6', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ffe58f' }}>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', fontWeight: 700 }}>OBSERVACIONES:</Text>
                                    <Text style={{ fontSize: '13px' }}>{ordenSeleccionada.notas}</Text>
                                </div>
                            )}

                            <Divider style={{ margin: '8px 0' }} />

                            {/* ASIGNACIÓN DE REPARTIDOR Y DESPACHO A MI RUTA */}
                            {ordenSeleccionada.estado === 'pendiente' && (
                                <div style={{ backgroundColor: '#eff6ff', padding: '12px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '6px', color: '#1e40af' }}>
                                        ASIGNAR REPARTIDOR / CONDUCTOR DE RUTA:
                                    </Text>
                                    <Select
                                        showSearch
                                        placeholder="Seleccionar repartidor..."
                                        style={{ width: '100%', marginBottom: '10px' }}
                                        value={
                                            repartidorSeleccionado !== null
                                                ? repartidorSeleccionado
                                                : (ordenSeleccionada?.repartidor_id ? String(ordenSeleccionada.repartidor_id) : undefined)
                                        }
                                        onChange={(value) => setRepartidorSeleccionado(value)}
                                        optionFilterProp="label"
                                        options={vendedores.map(v => {
                                            const nombreCompleto = `${v.nombre || ''} ${v.apellidos || ''}`.trim() || v.email || 'Sin Nombre';
                                            return {
                                                // ⚠️ Pasar tanto v.id como v.cognito_sub para asegurar la coincidencia con la BD
                                                value: String(v.id || v.cognito_sub),
                                                label: `🚚 ${nombreCompleto}`
                                            };
                                        })}
                                    />
                                    <Button
                                        type="primary"
                                        block
                                        icon={<TruckOutlined />}
                                        onClick={() => handleCambiarEstado(ordenSeleccionada.id, 'en_ruta')}
                                        style={{ backgroundColor: '#3b82f6', borderRadius: '8px', fontWeight: 'bold' }}
                                    >
                                        Despachar a "Mi Ruta"
                                    </Button>
                                </div>
                            )}

                            {/* ACCIONES DE ESTADO RESTRINGIDAS */}
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '6px' }}>

                                {ordenSeleccionada.estado === 'pendiente' && (
                                    <Button
                                        danger
                                        onClick={() => handleCambiarEstado(ordenSeleccionada.id, 'cancelado')}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        Anular Orden
                                    </Button>
                                )}

                                {ordenSeleccionada.estado === 'en_ruta' && (
                                    <>
                                        <Tag color="processing" style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 'bold' }}>
                                            🚚 En Ruta de Entrega
                                        </Tag>
                                        <Button
                                            danger
                                            onClick={() => handleCambiarEstado(ordenSeleccionada.id, 'cancelado')}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            Anular Orden
                                        </Button>
                                    </>
                                )}

                                {ordenSeleccionada.estado === 'completado' && (
                                    <div style={{ backgroundColor: '#f0fdf4', padding: '8px 16px', borderRadius: '8px', border: '1px solid #bbf7d0', textAlign: 'center', width: '100%' }}>
                                        <Text type="success" strong style={{ fontSize: '13px' }}>
                                            ✓ Este pedido ya fue entregado y facturado en caja.
                                        </Text>
                                    </div>
                                )}

                                {ordenSeleccionada.estado === 'cancelado' && (
                                    <div style={{ backgroundColor: '#fef2f2', padding: '8px 16px', borderRadius: '8px', border: '1px solid #fecaca', textAlign: 'center', width: '100%' }}>
                                        <Text type="danger" strong style={{ fontSize: '13px' }}>
                                            ✕ Este pedido se encuentra anulado.
                                        </Text>
                                    </div>
                                )}

                            </div>

                        </div>
                    )}
                </Modal>

            </div>
        </LayoutHome>
    );
};

export default Pedidos;